import { fetchKNNModel } from './fetchKNNModel';
import { enrichClientCases, initializeFetch } from './initializeFetchUtils';
import { fetchBadDataReport } from '../components/CaseReview/DataAnalysis/apiUtils';
import axiosInstance from '../fetchConfig/axiosConfig';
import { usePersistedDataStore } from '../components/store';
import { regenerateWorkupsByEvidenceTypes } from './regenerateWorkupsService.js';
import { toast } from '../common/toast';
import { isCaseSettled } from '../components/CaseReview/SummaryCaseTable/utils/helpers';
import logger from '../utils/logger';

/**
 * Filing generation (BAR / SCAR). Separate from case-analysis model invocation.
 * Loads client ids for the selected area, then asks the backend to generate one BAR filing PDF.
 *
 * @param {Object} options
 * @param {'BAR'|'SCAR'} options.filingType
 * @param {Object} options.locationParams
 * @param {string} options.propertyType
 * @param {Object} options.user
 * @param {Object} options.advancedSettings
 */
export const invokeFilingGeneration = async ({ filingType, locationParams, propertyType, user, advancedSettings = {} }) => {
  const { setIsFetching } = usePersistedDataStore.getState();
  setIsFetching(true);
  try {
    logger.log('🔄 [FILING_GENERATION] Loading client ids', { filingType, propertyType, userId: user?.userId });
    const result = await initializeFetch({
      locationParams,
      user,
      advancedSettings,
      overrides: {
        MuniCode: locationParams.MuniCode,
        TaxYear: locationParams.TaxYear,
        reportType: 'standard',
        runType: 'all_clients',
        UserId: user?.userId,
        showSettledCases: advancedSettings?.defaultRunTypes?.includes('settled'),
        scarOnly: advancedSettings?.defaultRunTypes?.includes('scarFiled') || false,
        unreviewed_flag: advancedSettings?.unreviewed_flag ?? 'False',
      },
      state: {
        propertyType,
        reportType: 'standard',
        runType: 'all_clients',
      },
      options: { skipLargeQueryWarning: true },
    });

    const pids = result?.data?.uids;
    if (!Array.isArray(pids)) {
      logger.error('[FILING_GENERATION] Client id load did not return pids', result?.reason || result?.error);
      return;
    }
    logger.log('[FILING_GENERATION] client pids', pids);
    const { data } = await axiosInstance.post('/generate_bar_filing', {
      parcelIds: pids,
      year: Number(locationParams.TaxYear),
      userId: user?.userId,
    });
    const binary = atob(data.pdfBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const blobUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = data.filename || 'BAR_Filing.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    toast.success('BAR filing downloaded');
  } catch (error) {
    logger.error('[FILING_GENERATION] Error generating filing', error);
  } finally {
    setIsFetching(false);
  }
};

/**
 * Unified function to invoke the appropriate model fetch based on report type
 * Handles all report types: standard, marketing, data_anazlysis, evidence_comparison
 * 
 * @param {Object} options - Configuration options
 * @param {Object} options.globalCompRef - Reference to global comp object
 * @param {Object} options.compStreamObject - Reference to comp stream object
 * @param {Function} options.processStreamWithWorker - Web Worker stream processing function
 * @param {Object} options.user - User object from useUser hook
 */
export const invokeModels = async ({
  globalCompRef,
  compStreamObject,
  processStreamWithWorker,
  user
}) => {
  // Get all persisted state directly from store
  const store = usePersistedDataStore.getState();
  const negotiationObj = store.negotiationObj;
  const setNegotiationObj = store.setNegotiationObj;
  const setDataAnalysisResult = store.setDataAnalysisResult;
  const setDataAnalysisColumns = store.setDataAnalysisColumns;
  const setDataAnalysisFilterModel = store.setDataAnalysisFilterModel;
  const setTotalCases = store.setTotalCases;
  const setIsFetching = store.setIsFetching;
  store.setCaseNotes([]);

  // Get advanced settings from localStorage
  let advancedSettings = {};
  try {
    advancedSettings = JSON.parse(localStorage.getItem('advancedSettings')) || {};
  } catch (e) {
    advancedSettings = {};
  }

  const reportType = negotiationObj?.reportType || 'standard';
  const uids = negotiationObj?.uids || [];

  // Set isFetching to true at the start for all report types
  setIsFetching(true);

  try {
    
    
    // Step 1: Enrich case rows (Propriety.CaseInfo via /enrich_client_cases) in parallel with KNN.
    // On HighTaxRun, enrichClientCases reorders `cases` to uids (orderEnrichedCasesByUids).
    // Non-client single parcel: backend attaches municipal RAR/EQ onto the minimal case row.
    // Client ID lists: initializeFetch -> LoadClientIds or load_high_tax_client_ids (HighTaxRun).
    const shouldEnrichCases =
      reportType === 'evidence_comparison'
      || (reportType !== 'marketing' && reportType !== 'data_analysis');

    if (shouldEnrichCases) {
      const enrichResult = await enrichClientCases({ validateAvIfmv: true });
      if (!enrichResult?.success) {
        setIsFetching(false);
        return;
      }
    }

    // Get updated negotiation object after enrichment
    const enrichedNegotiationObj = usePersistedDataStore.getState().negotiationObj;

    // Step 2: Invoke appropriate model based on report type
    switch (reportType) {
      case 'data_analysis': {
        // Get current bad data flags from store (already have store reference)
        const currentBadDataFlags = store.badDataFlags;
        const negotiationObjWithFlags = {
          ...enrichedNegotiationObj,
          BadDataFields: currentBadDataFlags.data,
          OutlierFields: currentBadDataFlags.outliers
        };
        logger.log('🔄 [INVOKE_MODELS] Step 2: Invoking data analysis (flag bad data)');

        const dataAnalysisResponse = await fetchBadDataReport(negotiationObjWithFlags, {
          initialInvocation: true,
          setDataAnalysisResult,
          setDataAnalysisColumns,
          setDataAnalysisFilterModel
        });

        if (dataAnalysisResponse) {
          const totalRows = dataAnalysisResponse.total_rows;
          const clientCount = dataAnalysisResponse.client_count;

          const updatedNegotiationObj = {
            ...enrichedNegotiationObj,
            totalRows,
            clientCount
          };

          // THIS IS LIKELY GONNA BE AN ISSUE
          setNegotiationObj(updatedNegotiationObj);
          setTotalCases(totalRows);
        }
        
        setIsFetching(false);

        break;
      }

      case 'evidence_comparison': {
        logger.log('🔄 [INVOKE_MODELS] Step 2: Invoking evidence comparison');
        const setLoadingModal = store.setLoadingModal;

        if (uids.length > 0 && negotiationObj?.TaxYear) {
          try {
            setLoadingModal({ label: 'Regenerating workups and saving comparables...', currentStep: 1, totalSteps: 2 });

            // console log the settled uids vs regular
            const cases = enrichedNegotiationObj.cases;
            const settledCases = cases.filter(caseItem => isCaseSettled(caseItem));
            const regularCases = cases.filter(caseItem => !isCaseSettled(caseItem));

            logger.log(`regenerating workups but SKIPPING ${settledCases.length} settled cases`);
            const parcelIdsToRegenerate = regularCases.map(caseItem => caseItem.parcel_id);

            const run = { ...enrichedNegotiationObj };
            delete run.cases;
            if (parcelIdsToRegenerate.length) {
              setLoadingModal({ label: 'Saving comparables...', currentStep: 1, totalSteps: 2 });
              try {
                const { data } = await axiosInstance.post('/run_evidence_autosave', {
                  parcelIds: parcelIdsToRegenerate,
                  run,
                });
                if (data?.errors?.length) {
                  toast.error(`Failed to autosave comps: ${data.errors.map((item) => item.error).join('; ')}`, { autoClose: false });
                  setIsFetching(false);
                  setLoadingModal(null);
                  break;
                }
              } catch (error) {
                logger.error('Failed to autosave comps', error);
                toast.error(`Failed to autosave comps: ${error.message}`, { autoClose: false });
                setIsFetching(false);
                setLoadingModal(null);
                break;
              }
            }

            setLoadingModal({ label: 'Regenerating workups...', currentStep: 1, totalSteps: 2 });
            const regenResult = await regenerateWorkupsByEvidenceTypes({
              parcelIds: parcelIdsToRegenerate,
              evidenceTypes: ['Unequal', 'Excessive'],
              userId: user?.userId,
            });
            logger.log("regenResult", regenResult);
            if (!regenResult?.success) {
              toast.error("Failed to regenerate workups breaking out: " + regenResult.message);
              setIsFetching(false);
              setLoadingModal(null);
              break;
            }
            setLoadingModal({ label: 'Running evidence comparison...', currentStep: 2, totalSteps: 2 });
            const evidenceResponse = await axiosInstance.post('/compare_case_evidence', {
              parcelIds: uids,
              taxYear: negotiationObj.TaxYear
            });
            logger.log("evidenceResponse", evidenceResponse);
            if (evidenceResponse.data.success) {
              store.setEvidenceComparisonData(evidenceResponse.data.evidence_data);
              setNegotiationObj(enrichedNegotiationObj);
              setTotalCases(uids.length);
            }
          } finally {
            setIsFetching(false);
            setLoadingModal(null);
          }
        } else {
          setIsFetching(false);
        }

        break;
      }

      case 'marketing':
      case 'standard':
      default: {
        logger.log('🔄 [INVOKE_MODELS] Step 2: Invoking KNN model');
        
        // Invoke KNN model fetch
        await fetchKNNModel({
          updateNegotiationObj: enrichedNegotiationObj,
          isInventoryUpdate: false,
          globalCompRef,
          compStreamObject,
          processStreamWithWorker,
          user,
          advancedSettings
        });

        break;
      }
    }

  } catch (error) {
    logger.error('❌ [INVOKE_MODELS] Error during unified model invocation:', error);
    // Ensure isFetching is set to false on error for all report types
    setIsFetching(false);
    throw error;
  }
};

